import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

function Quotes({ doraData: initialDoraData = null }) {
  const params = new URLSearchParams(useLocation().search);
  const sourcePage = (params.get('from') || '').slice(0, 200);
  const isReview = params.get('offer') === 'review';
  const sectionRef = React.useRef(null);
  const [email, setEmail] = useState('');
  const [fullname, setName] = useState('');
  const [number, setNumber] = useState('');
  const [company, setCompany] = useState('');
  const [service, setService] = useState((params.get('service') || '').slice(0, 120));
  const [description, setDescription] = useState('');
  const [budget, setBudget] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // DORA-specific fields - initialize from props if provided
  const [doraData, setDoraData] = useState(initialDoraData || {
    companyType: '',
    blockchainPlatforms: '',
    primaryTechFocus: '',
    web3Protocols: '',
    smartContractAuditStatus: '',
    ictRiskManagement: '',
    ictThirdPartyRisk: '',
    digitalResilienceTesting: '',
    ictIncidentReporting: '',
    informationSharing: '',
    criticalThirdPartyOversight: '',
    complianceDeadlineAwareness: '',
    timeline: '',
    preferredContactMethod: '',
    additionalComments: ''
  });

  // Update state when doraData prop changes
  React.useEffect(() => {
    if (initialDoraData) {
      setDoraData(initialDoraData);
      // Map basic DORA fields to quote form fields
      if (initialDoraData.contactName) setName(initialDoraData.contactName);
      if (initialDoraData.email) setEmail(initialDoraData.email);
      if (initialDoraData.phone) setNumber(initialDoraData.phone);
      if (initialDoraData.companyName) setCompany(initialDoraData.companyName);
      if (initialDoraData.companySize) setCompanySize(initialDoraData.companySize);
      if (initialDoraData.estimatedBudget) setBudget(initialDoraData.estimatedBudget);
    }
  }, [initialDoraData]);

  React.useEffect(() => {
    if (!isReview) return undefined;
    // Defer past App's scroll-to-top on route change (parent effects run after this one).
    const id = setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    return () => clearTimeout(id);
  }, [isReview]);


  const handleFormSubmit = (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    // Map DORA fields to quote form fields
    const mappedService = [
      doraData.companyType ? `Company Type: ${doraData.companyType}` : null,
      doraData.blockchainPlatforms ? `Blockchain Platforms: ${doraData.blockchainPlatforms}` : null,
      doraData.primaryTechFocus ? `Primary Tech Focus: ${doraData.primaryTechFocus}` : null,
      doraData.web3Protocols ? `Web3 Protocols: ${doraData.web3Protocols}` : null,
      doraData.smartContractAuditStatus ? `Smart Contract Audit Status: ${doraData.smartContractAuditStatus}` : null,
      doraData.timeline ? `Timeline: ${doraData.timeline}` : null,
      doraData.preferredContactMethod ? `Preferred Contact Method: ${doraData.preferredContactMethod}` : null
    ].filter(Boolean).join(' | ');

    const mappedDescription = [
      doraData.ictRiskManagement ? `ICT Risk Management: ${doraData.ictRiskManagement}` : null,
      doraData.ictThirdPartyRisk ? `ICT Third-Party Risk: ${doraData.ictThirdPartyRisk}` : null,
      doraData.digitalResilienceTesting ? `Digital Resilience Testing: ${doraData.digitalResilienceTesting}` : null,
      doraData.ictIncidentReporting ? `ICT Incident Reporting: ${doraData.ictIncidentReporting}` : null,
      doraData.informationSharing ? `Information Sharing: ${doraData.informationSharing}` : null,
      doraData.criticalThirdPartyOversight ? `Critical Third-Party Oversight: ${doraData.criticalThirdPartyOversight}` : null,
      doraData.complianceDeadlineAwareness ? `Compliance Deadline Awareness: ${doraData.complianceDeadlineAwareness}` : null,
      doraData.additionalComments ? `Additional Comments: ${doraData.additionalComments}` : null
    ].filter(Boolean).join('\n');

    const formData = {
      name: fullname,
      email,
      phone: number,
      company,
      service: [isReview && 'FREE 30-MINUTE REVIEW', service || mappedService].filter(Boolean).join(': '),
      description: [description || mappedDescription, sourcePage && `Source page: ${sourcePage}`].filter(Boolean).join('\n\n'),
      budget,
      companySize,
    };

   
    fetch('https://og-technologies.herokuapp.com/api/quote/', {
      method: 'POST',
      headers: {
       
      'Content-Type': 'application/json',
      
      },
      body: JSON.stringify(formData),
      
    })
      .then((response) => {
        if (response.ok) {
          // Successful submission
          console.log('Form submitted successfully');
          setSubmitSuccess(true)
        } else {
          setSubmitError('Something went wrong sending your request. Please try again or email hi@ogtechnologies.co.');
        }
      })
      .catch(() => {
        setSubmitError('Could not reach our server. Please check your connection or email hi@ogtechnologies.co.');
      })
      .finally(() => {
        // Reset the form submission state (if needed)
        setIsSubmitting(false);
      });

    // Set isSubmitting to true to disable the submit button while the form is being submitted
    setIsSubmitting(true);
  };

  return (
    <section ref={sectionRef} className="scroll-mt-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="relative bg-purple-600 py-10 px-8 md:py-16 md:px-12" data-aos="fade-up">
          <div className="absolute right-0 top-0 -ml-40 pointer-events-none" aria-hidden="true">
            <svg width="238" height="110" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="illustration-04" x1="369.483" y1="-84.633" x2="139.954" y2="-199.798" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#fff" stopOpacity=".01" />
                  <stop offset="1" stopColor="#fff" stopOpacity=".24" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <div className="relative flex flex-col lg:flex-row justify-between items-center">
            <div className="mb-6 lg:mr-16 lg:mb-0 text-center lg:text-left lg:w-1/2">
              <h3 className="h3 text-white mb-2">{isReview ? 'Book your free 30-minute review' : 'Request a Quote Form'}</h3>
              <p className="text-purple-200 text-lg">
                {isReview
                  ? 'Free and no obligation. Tell us your goal and timeline, and a specialist will contact you to schedule a 30-minute call.'
                  : 'Note: The more details you provide, the better we can customize our proposal to fit your needs'}
              </p>
              <br></br>
              <p className="text-purple-200 text-lg">Let's take this journey together. When you succeed, we succeed. Contact us today to unlock the full potential of your business and make technology your greatest asset.<br></br>

                With OG Technologies EU, you're not just buying a service. You're investing in a partnership. A partnership that grows with you, and for you. A partnership that puts your goals at the forefront, a partnership that cares.</p>
            </div>
            <form id="subscribe-form" className="w-full lg:w-1/2" onSubmit={handleFormSubmit}  >
              <div className="flex-col sm:flex-row justify-center max-w-xs mx-auto sm:max-w-md lg:max-w-none">
                <input
                  id="name"
                  type="name"
                  className="w-full appearance-none bg-purple-700 border border-purple-500 focus:border-purple-300 rounded-sm px-4 py-3 mb-2 sm:mb-2 sm:mr-2 text-white placeholder-purple-400"
                  placeholder="Name *"
                  aria-label="Name"
                  autoComplete="name"
                  required
                  value={fullname}
                  onChange={(event) => setName(event.target.value)}
                  disabled={isSubmitting}
                />
                <br></br>
                <input
                  id="email-input"
                  type="email"
                  className="w-full appearance-none bg-purple-700 border border-purple-500 focus:border-purple-300 rounded-sm px-4 py-3 mb-2 sm:mb-2 sm:mr-2 text-white placeholder-purple-400"
                  placeholder="Your work email *"
                  aria-label="Your email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={isSubmitting}
                />
                <input
                  id="phone-input"
                  className="w-full appearance-none bg-purple-700 border border-purple-500 focus:border-purple-300 rounded-sm px-4 py-3 mb-5 sm:mb-2 sm:mr-2 text-white placeholder-purple-400"
                  type="tel"
                  placeholder="Work phone (optional)"
                  aria-label="Your work phone number"
                  autoComplete="tel"
                  value={number}
                  onChange={(event) => setNumber(event.target.value)}
                  disabled={isSubmitting}
                />
                <input
                  id="company"
                  type="text"
                  className="w-full appearance-none bg-purple-700 border border-purple-500 focus:border-purple-300 rounded-sm px-4 py-3 mb-2 sm:mb-2 sm:mr-2 text-white placeholder-purple-400"
                  placeholder="Your company name"
                  aria-label="Your company name"
                  value={company}
                  onChange={(event) => setCompany(event.target.value)}
                  disabled={isSubmitting}
                />
                <input
                  id="company-size"
                  type="text"
                  className="w-full appearance-none bg-purple-700 border border-purple-500 focus:border-purple-300 rounded-sm px-4 py-3 mb-2 sm:mb-2 sm:mr-2 text-white placeholder-purple-400"
                  placeholder="Company Size (1-10, 11-50, 51-200, 201-500, 501+)"
                  aria-label="Company Size"
                  value={companySize}
                  onChange={(event) => setCompanySize(event.target.value)}
                  disabled={isSubmitting}
                />
                <input
                  id="service"
                  type="text"
                  className="w-full appearance-none bg-purple-700 border border-purple-500 focus:border-purple-300 rounded-sm px-4 py-3 mb-2 sm:mb-2 sm:mr-2 text-white placeholder-purple-400"
                  placeholder="Service(s) Interested In"
                  aria-label="Service(s) Interested In"
                  value={service}
                  onChange={(event) => setService(event.target.value)}
                  disabled={isSubmitting}
                />
                <textarea
                  id="description"
                  rows={4}
                  className="w-full appearance-none bg-purple-700 border border-purple-500 focus:border-purple-300 rounded-sm px-4 py-3 mb-2 sm:mb-2 sm:mr-2 text-white placeholder-purple-400"
                  placeholder="What are you trying to achieve? (goals, deadline, systems involved)"
                  aria-label="Brief Description of Your Project:"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  disabled={isSubmitting}
                />
                <input
                  id="budget"
                  type="text"
                  className="w-full appearance-none bg-purple-700 border border-purple-500 focus:border-purple-300 rounded-sm px-4 py-3 mb-2 sm:mb-2 sm:mr-2 text-white placeholder-purple-400"
                  placeholder="Estimated Budget"
                  aria-label="Estimated Budget"
                  value={budget}
                  onChange={(event) => setBudget(event.target.value)}
                  disabled={isSubmitting}
                />
                <button
                  id="subscribe-button"
                  type="submit"
                  className="btn text-purple-600 bg-purple-100 mb-2 hover:bg-white shadow"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : isReview ? 'Book my free review' : 'Request a Quote'}
                </button>
              </div>

              {/* Success message */}
              {submitError && (
                <div role="alert" className="mt-2 rounded-lg bg-red-700 text-white px-4 py-3">{submitError}</div>
              )}

               {submitSuccess && (
                      <div style={{ backgroundColor: 'green', color: 'white', padding: '10px', borderRadius: '10px' }}>
                        Success! Your Quote Request has been submitted.
                      </div>
                    )}
            </form>

          </div>

        </div>

      </div>
    </section>
  );
}

export default Quotes;
